/** OrderDetailPage — /orders/:id investigation view: customer, financials, gateway + reconcile, FSM timeline. */
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { cn } from '@/src/infrastructure/lib/utils';
import { downloadInvoicePdf, getAdminOrder, refreshAdminOrderGateway, type AdminOrderDetail } from '@/src/infrastructure/lib/api';
import { orderTone, rupees } from './OrdersPage';
import { RefundPaymentModal } from '../users/components/RefundPaymentModal';
import { ConfirmFaultModal } from '../users/components/ConfirmFaultModal';
import type { AdminPaymentRow } from '../users/types';

type Invoice = AdminOrderDetail['payment']['invoices'][number];

/** Adapts an order's invoice to the row shape the existing Users→Payments refund modals take. */
function toPaymentRow(o: AdminOrderDetail, inv: Invoice): AdminPaymentRow {
  return {
    id: inv.id, invoiceNumber: inv.invoiceNumber, userId: o.user.id, invitationId: null, amount: inv.totalPaise,
    currency: inv.currency, gateway: 'RAZORPAY', gatewayOrderId: inv.providerOrderId ?? '', gatewayPaymentId: inv.paidPaymentId,
    status: 'PAID', plan: o.collectionCode, refundedAt: null, refundAmount: null, refundReason: null, refundedById: null, createdAt: o.createdAt,
    pack: (o as any).pack ?? (inv as any).pack ?? null,
  };
}

const refunds = (inv: Invoice) => inv.payments.flatMap((p) => p.refunds);
const refundInFlight = (inv: Invoice) => refunds(inv).some((r) => r.status === 'REQUESTED' || r.status === 'PROCESSING');
const refundDone = (inv: Invoice) => refunds(inv).some((r) => r.status === 'SUCCESS') || inv.status === 'VOID';

const RECONCILABLE = new Set(['PAYMENT_PENDING', 'CHECKOUT_CREATED', 'PAYMENT_COMPLETED', 'FULFILLMENT_IN_PROGRESS']);
const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleString('en-IN') : '—');

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-2xl border border-border-subtle bg-surface-low/40 p-4 space-y-2">
      <h2 className="text-xs uppercase tracking-wider text-on-surface-variant">{title}</h2>
      {children}
    </section>
  );
}
const Row = ({ k, v }: { k: string; v: React.ReactNode }) => (
  <div className="flex justify-between gap-4 text-sm"><span className="text-on-surface-variant">{k}</span><span className="text-right break-all">{v ?? '—'}</span></div>
);

export default function OrderDetailPage() {
  const { id = '' } = useParams();
  const [o, setO] = useState<AdminOrderDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [refundInv, setRefundInv] = useState<Invoice | null>(null);
  const [faultInv, setFaultInv] = useState<Invoice | null>(null);

  const load = useCallback(() => {
    getAdminOrder(id).then(setO).catch((e) => setError(e instanceof Error ? e.message : 'Could not load order'));
  }, [id]);
  useEffect(load, [load]);

  const reconcile = async () => {
    if (!window.confirm('Ask Razorpay for this order\'s payment status and advance it if paid?')) return;
    setBusy(true);
    setNotice(null);
    try {
      const r = await refreshAdminOrderGateway(id);
      setNotice(r.message);
      load();
    } catch (e) {
      setNotice(e instanceof Error ? e.message : 'Reconcile failed');
    } finally {
      setBusy(false);
    }
  };

  if (error) return (
    <div className="p-8 space-y-3">
      <Link to="/orders" className="text-sm underline">← All orders</Link>
      <p role="alert" className="text-rose-600">{error}</p>
    </div>
  );
  if (!o) return <p className="p-8 text-on-surface-variant">Loading…</p>;

  return (
    <div className="p-6 md:p-8 space-y-6">
      <Link to="/orders" className="text-sm underline">← All orders</Link>
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-display font-bold font-mono">{o.orderNumber}</h1>
          <span className={cn('rounded-full border px-2 py-0.5 text-xs', orderTone(o.status))}>{o.status}</span>
        </div>
        {RECONCILABLE.has(o.status) && (
          <button onClick={reconcile} disabled={busy} className="rounded-full border px-4 py-2 text-sm disabled:opacity-50">
            {busy ? 'Checking…' : 'Reconcile with Razorpay'}
          </button>
        )}
      </header>
      {notice && <p role="status" className="text-sm">{notice}</p>}

      {(o as any).walletCredit && (
        <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-3 text-sm text-emerald-300">
          Paid after expiry — ₹{(((o as any).walletCredit.amountPaise ?? 0) / 100).toLocaleString('en-IN')} added to user&apos;s wallet ({((o as any).walletCredit.credits ?? 0).toLocaleString('en-IN')} credits).
        </div>
      )}

      {o.refundRequests.filter((r) => r.status === 'PENDING_REVIEW').map((r) => (
        <div key={r.id} className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-300 flex items-center justify-between">
          <span>Customer refund request pending review</span>
          <Link to={`/refund-requests/${r.id}`} className="underline font-semibold">Review request →</Link>
        </div>
      ))}

      <div className="grid md:grid-cols-2 gap-4">
        <Card title="Customer & product">
          <Row k="Name" v={<Link className="underline" to={`/users/${o.user.id}`}>{o.user.name}</Link>} />
          <Row k="Email" v={o.user.email} />
          <Row k="Phone" v={o.user.phone} />
          <Row k="Product" v={o.planCatalog?.displayName ?? o.creditPack?.displayName ?? o.collectionCode} />
          <Row k="Collection" v={o.collectionCode} />
        </Card>

        <Card title="Financials">
          <Row k="Amount" v={rupees(o.amountPaise)} />
          <Row k="Discount" v={o.discountPaise ? `${rupees(o.discountPaise)} (${o.appliedCouponCode ?? 'coupon'})` : '—'} />
          {o.payment.invoices.map((inv) => (
            <div key={inv.id} className="border-t border-border-subtle pt-2 space-y-1">
              <Row k="Invoice" v={
                <>
                  {inv.invoiceNumber} · {inv.status}{' '}
                  <button className="underline" onClick={() => downloadInvoicePdf(inv.id, inv.invoiceNumber).catch((e) => setNotice(e instanceof Error ? e.message : 'PDF download failed'))}>
                    PDF
                  </button>
                </>
              } />
              {inv.payments.map((p) => (
                <Row key={p.id} k={`${p.providerPaymentId} (${p.method ?? '?'})`}
                  v={`${p.status} · captured ${rupees(p.capturedAmountPaise ?? 0)} · refunded ${rupees(p.refundedAmountPaise)}`} />
              ))}
            </div>
          ))}
          {o.payment.invoices.flatMap(refunds).map((r) => <Row key={r.id} k="Refund" v={`${rupees(r.amountPaise)} · ${r.status}`} />)}
          {o.payment.invoices.filter((i) => i.source === 'ONE_TIME' && i.status === 'PAID' && !refundInFlight(i) && !refundDone(i)).map((inv) => (
            <div key={inv.id} className="flex gap-2 pt-1">
              <button onClick={() => setFaultInv(inv)} className="rounded-full border border-amber-400/40 px-3 py-1 text-xs text-amber-300">Confirm fault</button>
              <button onClick={() => setRefundInv(inv)} className="rounded-full border border-red-400/40 px-3 py-1 text-xs text-red-300">Refund</button>
            </div>
          ))}
          {o.refundRequests.map((r) => <Row key={r.id} k="Refund request" v={<Link className="underline" to={`/refund-requests/${r.id}`}>{r.status}</Link>} />)}
          {o.refundRequests.length === 0 && <Row k="Refund requests" v={<Link className="underline" to="/refund-requests">Open queue</Link>} />}
        </Card>

        <Card title="Gateway & fulfillment">
          {o.payment.invoices.map((inv) => <Row key={inv.id} k="Razorpay order" v={inv.providerOrderId} />)}
          <Row k="Entitlement granted" v={fmt(o.fulfillment.entitlementGrantedAt)} />
          <Row k="Publish credits" v={o.fulfillment.publishCreditGranted} />
          <Row k="Source" v={o.fulfillment.entitlementSource} />
          <Row k="Correlation id" v={o.correlationId} />
          {o.fault && <Row k="Fault" v={`${o.fault.source}: ${o.fault.reason}`} />}
        </Card>

        <Card title="Status timeline">
          <ol className="space-y-2">
            {o.transitions.map((t) => (
              <li key={t.id} className={cn('text-sm', !t.accepted && 'opacity-60')}>
                <span className="font-medium">{t.fromStatus ?? 'START'} → {t.toStatus}</span>{!t.accepted && ' (rejected)'}
                <div className="text-xs text-on-surface-variant">{t.trigger} · {fmt(t.occurredAt)}{t.reason ? ` · ${t.reason}` : ''}</div>
              </li>
            ))}
          </ol>
        </Card>
      </div>
      {refundInv && <RefundPaymentModal open payment={toPaymentRow(o, refundInv)} onClose={() => { setRefundInv(null); load(); }} />}
      {faultInv && <ConfirmFaultModal open payment={toPaymentRow(o, faultInv)} onClose={() => { setFaultInv(null); load(); }} />}
    </div>
  );
}
